/**
 * Keeps a tap on the mobile menu button that lands before the navbar hydrates
 * (AHD-14173).
 *
 * The button is in the prerendered HTML, so it shows long before React is
 * listening. A tap in that window did nothing, not even later: measured on
 * prod at 6x CPU on a Pixel 7 profile, the window between DOMContentLoaded and
 * `header[data-hydrated]` was 1.6 s on /pricing and 1.9 s on the homepage, and
 * a tap at DOMContentLoaded never opened the menu. On Android that read as a
 * button that "does not react always" and drew repeat taps.
 *
 * The `<head>` script below records such a tap; the navbar calls
 * `takeEarlyNavTap()` once hydrated and opens the menu for it. Raw inline
 * script in `<head>` for the same reason as `DARK_SHELL_HEAD_SCRIPT`: it has to
 * be listening before the body is parsed.
 */

declare global {
  interface Window {
    /** `timeStamp` of the last pre-hydration tap on the menu button. */
    __aheadNavTap?: number | null
    /** Removes the head script's listener. */
    __aheadNavTapOff?: () => void
  }
}

export const NAV_EARLY_TAP_HEAD_SCRIPT = `(function(){var w=window,d=document;function h(e){var t=e.target;if(t&&t.closest&&t.closest("[data-nav-menu-toggle]"))w.__aheadNavTap=e.timeStamp}d.addEventListener("click",h,true);w.__aheadNavTapOff=function(){d.removeEventListener("click",h,true)}})()`

/**
 * Stops recording and returns the `timeStamp` of a tap that landed before
 * hydration, or null.
 */
export function takeEarlyNavTap(): number | null {
  window.__aheadNavTapOff?.()
  window.__aheadNavTapOff = undefined
  const stamp = window.__aheadNavTap ?? null
  window.__aheadNavTap = null
  return stamp
}
